import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { ScheduleType } from '@src/models/interfaces/schedule.interface';

import useSidebarEventSearch from '../../../hooks/useSidebarEventSearch';
import useSidebarScheduleSearch from '../../../hooks/useSidebarScheduleSearch';
import useSidebarTaskSearch from '../../../hooks/useSidebarTaskSearch';
import useSidebarUserSearch from '../../../hooks/useSidebarUserSearch';
import { buildSearchEntries } from '../../../utils/buildSearchEntries';
import type { MenuSearchItem, SidebarSearchUser } from '../types';

const REMOTE_SEARCH_FILTERS = new Set(['users', 'events', 'schedules', 'routes', 'tasks']);
const EMPTY_SCHEDULE_TYPES: ScheduleType[] = [];
const SCHEDULE_TYPES_BY_FILTER: Record<string, ScheduleType[]> = {
  all: [ScheduleType.Schedule, ScheduleType.CheckpointRoute],
  schedules: [ScheduleType.Schedule],
  routes: [ScheduleType.CheckpointRoute],
};

const SEARCH_ERRORS = [
  ['users', 'sidebar.search.error'],
  ['events', 'sidebar.search.error.events'],
  ['schedules', 'sidebar.search.error.schedules'],
  ['tasks', 'sidebar.search.error.tasks'],
] as const;

export interface UseSidebarSearchEntriesParams {
  open: boolean;
  orgId?: string;
  searchTerm: string;
  searchFilter: string;
  searchableItems: MenuSearchItem[];
  canSearchUsers: boolean;
  canSearchEvents: boolean;
  canSearchSchedules: boolean;
  canSearchTasks: boolean;
}

export interface SidebarSearchEntries {
  entries: ReturnType<typeof buildSearchEntries>;
  loading: boolean;
  errorId?: string;
  isRemoteSearchFilter: boolean;
  hasRemoteSearchTerm: boolean;
}

const normalizeSearchText = (value: string, locale: string) =>
  value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase(locale);

const useSidebarSearchEntries = ({
  open,
  orgId,
  searchTerm,
  searchFilter,
  searchableItems,
  canSearchUsers,
  canSearchEvents,
  canSearchSchedules,
  canSearchTasks,
}: UseSidebarSearchEntriesParams): SidebarSearchEntries => {
  const intl = useIntl();
  const normalizedSearchTerm = normalizeSearchText(searchTerm, intl.locale);
  const isRemoteSearchFilter = REMOTE_SEARCH_FILTERS.has(searchFilter);
  const hasRemoteSearchTerm = normalizedSearchTerm.length >= 2;
  const scheduleSearchTypes = SCHEDULE_TYPES_BY_FILTER[searchFilter] ?? EMPTY_SCHEDULE_TYPES;

  const shouldSearchUsers =
    open && canSearchUsers && (searchFilter === 'all' || searchFilter === 'users') && hasRemoteSearchTerm;
  const shouldSearchEvents =
    open &&
    canSearchEvents &&
    (searchFilter === 'all' || searchFilter === 'events') &&
    hasRemoteSearchTerm;
  const shouldSearchSchedules =
    open && canSearchSchedules && scheduleSearchTypes.length > 0 && hasRemoteSearchTerm;
  const shouldSearchTasks =
    open && canSearchTasks && (searchFilter === 'all' || searchFilter === 'tasks') && hasRemoteSearchTerm;

  const {
    users: userSearchResults,
    loading: isUserSearchLoading,
    error: hasUserSearchError,
  } = useSidebarUserSearch({ enabled: shouldSearchUsers, orgId, searchTerm }) as {
    users: SidebarSearchUser[];
    loading: boolean;
    error: boolean;
  };
  const {
    events: eventSearchResults,
    loading: isEventSearchLoading,
    error: hasEventSearchError,
  } = useSidebarEventSearch({ enabled: shouldSearchEvents, orgId, searchTerm });
  const {
    schedules: scheduleSearchResults,
    loading: isScheduleSearchLoading,
    error: hasScheduleSearchError,
  } = useSidebarScheduleSearch({
    enabled: shouldSearchSchedules,
    orgId,
    searchTerm,
    types: scheduleSearchTypes,
  });
  const {
    tasks: taskSearchResults,
    loading: isTaskSearchLoading,
    error: hasTaskSearchError,
  } = useSidebarTaskSearch({ enabled: shouldSearchTasks, orgId, searchTerm });

  const menuResults = useMemo(() => {
    if (isRemoteSearchFilter || !normalizedSearchTerm) return [];
    return searchableItems.filter(item =>
      normalizeSearchText(item.label, intl.locale).includes(normalizedSearchTerm),
    );
  }, [intl.locale, isRemoteSearchFilter, normalizedSearchTerm, searchableItems]);

  const entries = useMemo(
    () =>
      buildSearchEntries({
        menuItems: menuResults,
        users: shouldSearchUsers ? userSearchResults : [],
        events: shouldSearchEvents ? eventSearchResults : [],
        schedules: shouldSearchSchedules ? scheduleSearchResults : [],
        tasks: shouldSearchTasks ? taskSearchResults : [],
      }),
    [
      eventSearchResults,
      menuResults,
      scheduleSearchResults,
      shouldSearchEvents,
      shouldSearchSchedules,
      shouldSearchTasks,
      shouldSearchUsers,
      taskSearchResults,
      userSearchResults,
    ],
  );

  const errors: Record<(typeof SEARCH_ERRORS)[number][0], boolean> = {
    users: shouldSearchUsers && hasUserSearchError,
    events: shouldSearchEvents && hasEventSearchError,
    schedules: shouldSearchSchedules && hasScheduleSearchError,
    tasks: shouldSearchTasks && hasTaskSearchError,
  };
  const errorId = SEARCH_ERRORS.find(([source]) => errors[source])?.[1];

  const loading =
    (shouldSearchUsers && isUserSearchLoading) ||
    (shouldSearchEvents && isEventSearchLoading) ||
    (shouldSearchSchedules && isScheduleSearchLoading) ||
    (shouldSearchTasks && isTaskSearchLoading);

  return { entries, loading, errorId, isRemoteSearchFilter, hasRemoteSearchTerm };
};

export default useSidebarSearchEntries;
